import { RouterModule } from '@angular/router';
import { NgModule, ModuleWithProviders } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { EditorModule } from '@tinymce/tinymce-angular';
import { TranslateModule } from '@ngx-translate/core';
import { TabsModule, PaginationModule, BsDatepickerConfig, BsDatepickerModule, PaginationConfig, TooltipModule } from 'ngx-bootstrap';
import { ToastrModule } from 'ngx-toastr';

import { AuthService } from './auth.service';
import {
  AdjustmentTypesService,
  AppliedAdjustmentsService,
  SessionService,
  ContainersService,
  EulaService,
  CurrenciesService,
  NotificationService,
  TbasService,
  TbaEvaluationMethodsService,
  AdjustmentEffectivenessService,
  TransactionsComparabilityService,
  RangeTypesService,
  RangeResultsService,
  BackgroundInfoService,
  FinancialDataService,
  FunctionalAnalysesService,
  PbasService,
  NumberFormatService,
  UtilsService,
  PartyRolesService,
  TransactionsService,
  UncontrolledEntitiesService,
  PrimaryFunctionsService,
  CountriesService,
  StudiesService,
  UncontrolledTransactionsService,
  WtprService,
  CurrencyConversionService,
  FileLoaderService,
  LegalEntityLocationsService,
  TransactionsMethodEvaluationService,
  AllEvaluationMethodService,
  CompSearchService,
  ComplianceCentralService,
  InternalLocalFileReportService,
  CompSearchRangeService,
  ReportRepositoryService,
  TableDataService,
} from './services';

import { HeaderComponent } from '../header/header.component';
import { ErrorMessageComponent } from './components/error-message/error-message.component';
import { ModalWindowComponent } from './components/modal/modal-window/modal-window.component';
import { ModalInputErrorMsgComponent } from './components/modal/modal-input-error-msg/modal-input-error-msg.component';
import { SaveLibraryDataModalComponent } from './components/library-data/save-library-data-modal/save-library-data-modal.component';
import {
  BaseBackgroundInfoComponent
} from './components/background-info/base-background-info/base-background-info.component';
import {
  BackgroundInfoTabsComponent
} from './components/background-info/background-info-tabs/background-info-tabs.component';
import { FinancialsTableComponent } from '../entity/entity-financials/financials-table/financials-table.component';
import {
  BackgroundInfoGalleryComponent
} from './components/background-info/background-info-gallery/background-info-gallery.component';

import { DisplayRtfTextComponent } from './components/display-rtf-text/display-rtf-text.component';
import { WorkingReportRepositoryComponent } from './components/reports/working-report-repository/working-report-repository.component';
import { FinalReportRepositoryComponent } from './components/reports/final-report-repository/final-report-repository.component';

import { XbsNumberFormatPipe } from './pipes/xbs-number-format-pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { TabBreakPipe } from './pipes/tab-break.pipe';
import { TpSafeService } from './services/tp-safe.service';
import { TpReportGeneratorService } from './services/tp-report-generator.service';
import { FormService } from './services/form.service';
import { TableRowComponent } from './components/table-row/table-row.component';
import { AuthPolicyDirective } from './directives/authorization-policy.directive';
import { XbsPliFormatPipe } from './pipes/xbs-pli-format';
import { XbsCurencyConversionFormatPipe } from './pipes/xbs-currency-conversion-format';
import { xbsTbaPliFormatPipe } from './pipes/xbs-tbaPli-format';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgSelectModule,
    RouterModule,
    EditorModule,
    TranslateModule,
    TabsModule,
    FormsModule,
    PaginationModule,
    BsDatepickerModule.forRoot(),
    ToastrModule.forRoot(),
    TooltipModule.forRoot(),
  ],
  providers: [
    DatePipe,
    DecimalPipe,
    BsDatepickerConfig,
    PaginationConfig
  ],
  declarations: [
    HeaderComponent,
    ErrorMessageComponent,
    BackgroundInfoTabsComponent,
    FinancialsTableComponent,
    BackgroundInfoGalleryComponent,
    ModalInputErrorMsgComponent,
    ModalWindowComponent,
    SafeHtmlPipe,
    SaveLibraryDataModalComponent,
    TabBreakPipe,
    XbsNumberFormatPipe,
    XbsPliFormatPipe,
    xbsTbaPliFormatPipe,
    XbsCurencyConversionFormatPipe,
    DisplayRtfTextComponent,
    BaseBackgroundInfoComponent,
    WorkingReportRepositoryComponent,
    FinalReportRepositoryComponent,
    TableRowComponent,
    AuthPolicyDirective,
  ],
  exports: [
    CommonModule,
    NgSelectModule,

    ErrorMessageComponent,
    HeaderComponent,
    ModalInputErrorMsgComponent,
    ModalWindowComponent,
    SaveLibraryDataModalComponent,
    TranslateModule,
    BackgroundInfoTabsComponent,
    FinancialsTableComponent,
    BackgroundInfoGalleryComponent,
    PaginationModule,
    TabsModule,
    SafeHtmlPipe,
    TabBreakPipe,
    XbsNumberFormatPipe,
    XbsPliFormatPipe,
    xbsTbaPliFormatPipe,
    XbsCurencyConversionFormatPipe,
    DisplayRtfTextComponent,
    BsDatepickerModule,
    WorkingReportRepositoryComponent,
    FinalReportRepositoryComponent,
    TooltipModule,
    TableRowComponent,
    AuthPolicyDirective
  ],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: SharedModule,
      providers: [
        BackgroundInfoService,
        AdjustmentTypesService,
        AppliedAdjustmentsService,
        AuthService,
        EulaService,
        ContainersService,
        SessionService,
        NotificationService,
        CurrenciesService,
        TbasService,
        TbaEvaluationMethodsService,
        AdjustmentEffectivenessService,
        TransactionsComparabilityService,
        RangeTypesService,
        RangeResultsService,
        FinancialDataService,
        FunctionalAnalysesService,
        PbasService,
        NumberFormatService,
        UtilsService,
        TransactionsService,
        PartyRolesService,
        UncontrolledEntitiesService,
        PrimaryFunctionsService,
        CountriesService,
        TpSafeService,
        TpReportGeneratorService,
        StudiesService,
        UncontrolledTransactionsService,
        WtprService,
        CurrencyConversionService,
        FileLoaderService,
        LegalEntityLocationsService,
        TransactionsMethodEvaluationService,
        AllEvaluationMethodService,
        CompSearchService,
        ComplianceCentralService,
        InternalLocalFileReportService,
        CompSearchRangeService,
        ReportRepositoryService,
        FormService,
        TableDataService
      ]
    };
  };
}
